import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import { describeSegment, type ConditionEntry } from '@shared/segments'
import {
  ACTIONS_FOR_ENTITY,
  ACTION_LABELS,
  ASSIGNMENT_FILTERS,
  ASSIGNMENT_MODES,
  AUTOMATION_TEMPLATES,
  TEMPLATE_CATEGORIES,
  TRIGGER_ENTITY,
  TRIGGER_LABELS,
  fieldsForTrigger,
  isAssignmentMode,
  validateAutomationRule,
  type AutomationAction,
  type AutomationActionType,
  type AutomationTemplate,
  type AutomationTrigger,
} from '@shared/automationRules'
import { api, ApiError, type AutomationRuleView } from '../lib/api'
import { SegmentBuilder } from './SegmentBuilder'
import { AutomationCanvas, estimateCanvasHeight, type CanvasChain } from './AutomationCanvas'
import { ReadOnlyNotice } from './ReadOnlyNotice'

export const NEEDS_VALUE: AutomationActionType[] = [
  'send_message',
  'forward_message',
  'create_ticket',
  'assign_ticket',
  'assign_chat',
  'add_ticket_label',
  'remove_ticket_label',
  'add_chat_label',
  'remove_chat_label',
  'send_email',
]

/** Renders a value input too, but `validateAutomationRule` does not require it be filled in. */
export const OPTIONAL_VALUE: AutomationActionType[] = ['flag_message', 'notify_assignee']

export const VALUE_LABEL: Partial<Record<AutomationActionType, string>> = {
  send_message: 'Message text',
  forward_message: 'Destination chat id',
  create_ticket: 'Category',
  add_ticket_label: 'Label name',
  remove_ticket_label: 'Label name',
  add_chat_label: 'Label name',
  remove_chat_label: 'Label name',
  send_email: 'Recipient email',
  flag_message: 'Reason (optional)',
  notify_assignee: 'Notification text (optional)',
  notify_webhook: 'Webhook URL (Slack incoming webhooks work too)',
}

export const ASSIGNMENT_MODE_LABEL: Record<(typeof ASSIGNMENT_MODES)[number], string> = {
  round_robin: 'Round robin',
  least_loaded: 'Least loaded',
}

export const ASSIGNMENT_FILTER_LABEL: Record<(typeof ASSIGNMENT_FILTERS)[number], string> = {
  shift: 'Whoever is on shift',
  status: 'Whoever is online',
  both: 'On shift AND online',
}

/**
 * When something happens, check conditions, then do one or more things automatically.
 *
 * A separate engine from `Workflows.tsx`'s ticket routing on purpose — that screen is one
 * small, complete thing (message text → category, priority, SLA); this is the general
 * trigger/conditions/actions layer sitting above it. Both share the same underlying
 * condition-matching engine (`@shared/segments`), just pointed at different fields.
 */
export function AutomationRules() {
  const queryClient = useQueryClient()
  const [editingId, setEditingId] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [galleryOpen, setGalleryOpen] = useState(false)
  const [startTemplate, setStartTemplate] = useState<AutomationTemplate | null>(null)

  const rulesQuery = useQuery({ queryKey: ['automation-rules'], queryFn: api.automationRules, retry: false })
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['automation-rules'] })

  const remove = useMutation({ mutationFn: (id: string) => api.deleteAutomationRule(id), onSuccess: refresh })
  const toggle = useMutation({
    mutationFn: (input: { id: string; enabled: boolean }) =>
      api.updateAutomationRule(input.id, { enabled: input.enabled }),
    onSuccess: refresh,
  })
  const reorder = useMutation({ mutationFn: api.reorderAutomationRules, onSuccess: refresh })

  if (rulesQuery.isPending) return <div className="cad-card" style={{ height: '200px' }} />
  if (rulesQuery.isError) {
    const message = rulesQuery.error instanceof ApiError ? rulesQuery.error.message : 'Unavailable.'
    return (
      <div className="cad-card">
        <p className="cad-muted">{message}</p>
      </div>
    )
  }

  const { rules, canManage } = rulesQuery.data!

  const move = (index: number, direction: -1 | 1) => {
    const next = [...rules]
    const target = index + direction
    if (target < 0 || target >= next.length) return
    ;[next[index], next[target]] = [next[target], next[index]]
    reorder.mutate(next.map((r) => r.id))
  }

  return (
    <>
      {!canManage && <ReadOnlyNotice what="automation rules" />}
      <header className="stack gap-1">
        <span className="cad-eyebrow">Automation</span>
        <div className="row between wrap gap-3" style={{ alignItems: 'center' }}>
          <h1 className="cad-title">{rules.length} rule{rules.length === 1 ? '' : 's'}</h1>
          {canManage && !creating && !galleryOpen && !editingId && (
            <button type="button" className="cad-btn" onClick={() => setGalleryOpen(true)}>
              New rule
            </button>
          )}
        </div>
        <p className="cad-muted" style={{ maxWidth: '64ch' }}>
          When something happens — a new chat, a ticket closing, a task getting assigned —
          check conditions, then do one or more things automatically. Rules run in the order
          below and never trigger each other, so a chain of rules cannot loop forever.
        </p>
      </header>

      {galleryOpen && (
        <TemplateGallery
          onPick={(template) => {
            setStartTemplate(template)
            setGalleryOpen(false)
            setCreating(true)
          }}
          onBlank={() => {
            setStartTemplate(null)
            setGalleryOpen(false)
            setCreating(true)
          }}
          onCancel={() => setGalleryOpen(false)}
        />
      )}

      {creating && (
        <RuleEditor
          template={startTemplate}
          onDone={() => {
            setCreating(false)
            setStartTemplate(null)
            void refresh()
          }}
          onCancel={() => {
            setCreating(false)
            setStartTemplate(null)
          }}
        />
      )}

      <section className="cad-card">
        {(remove.error || toggle.error) && (
          <p className="cad-note" style={{ borderColor: 'var(--cad-bad)', marginBottom: '12px' }}>
            {(() => {
              const err = remove.error ?? toggle.error
              return err instanceof ApiError || err instanceof Error ? err.message : 'That did not go through.'
            })()}
          </p>
        )}
        {rules.length === 0 ? (
          <p className="cad-muted">
            No rules yet.{' '}
            {canManage
              ? '“New rule” above opens a gallery of the common ones — round-robin assignment, escalating a ticket left open too long, flagging messages that mention cancelling — or you can start from a blank one.'
              : 'Rules run automatically on new chats and tickets. Your role cannot add them.'}
          </p>
        ) : (
          <div className="stack gap-2">
            {rules.map((rule, index) =>
              editingId === rule.id ? (
                <RuleEditor
                  key={rule.id}
                  rule={rule}
                  onDone={() => {
                    setEditingId(null)
                    void refresh()
                  }}
                  onCancel={() => setEditingId(null)}
                />
              ) : (
                <RuleRow
                  key={rule.id}
                  rule={rule}
                  canManage={canManage}
                  isFirst={index === 0}
                  isLast={index === rules.length - 1}
                  reordering={reorder.isPending}
                  onMoveUp={() => move(index, -1)}
                  onMoveDown={() => move(index, 1)}
                  onEdit={() => setEditingId(rule.id)}
                  onDelete={() => {
                    if (confirm(`Delete the "${rule.name}" rule?

It stops running immediately and its configuration cannot be recovered.`)) remove.mutate(rule.id)
                  }}
                  onToggle={() => toggle.mutate({ id: rule.id, enabled: !rule.enabled })}
                />
              ),
            )}
          </div>
        )}
      </section>
    </>
  )
}

function RuleRow({
  rule,
  canManage,
  isFirst,
  isLast,
  reordering,
  onMoveUp,
  onMoveDown,
  onEdit,
  onDelete,
  onToggle,
}: {
  rule: AutomationRuleView
  canManage: boolean
  isFirst: boolean
  isLast: boolean
  reordering: boolean
  onMoveUp: () => void
  onMoveDown: () => void
  onEdit: () => void
  onDelete: () => void
  onToggle: () => void
}) {
  const [showRuns, setShowRuns] = useState(false)
  const [showCanvas, setShowCanvas] = useState(false)
  const runs = useQuery({
    queryKey: ['automation-rule-runs', rule.id],
    queryFn: () => api.automationRuleRuns(rule.id),
    enabled: showRuns,
    retry: false,
  })

  const fields = fieldsForTrigger(rule.trigger)
  const description = describeSegment({ match: rule.match, conditions: rule.conditions }, fields)
  const canvasChains: CanvasChain[] = [
    {
      id: rule.id,
      segments: [
        {
          criteriaLabel: rule.conditions.length > 0 ? description : null,
          delayLabel: rule.delaySeconds ? `Wait ${Math.round(rule.delaySeconds / 60)}m` : undefined,
          disabled: !rule.enabled,
          actions: rule.actions.map((a, i) => ({ key: `${rule.id}-${i}`, label: ACTION_LABELS[a.type] })),
        },
      ],
    },
  ]

  return (
    <div className="cad-card" style={{ padding: '12px 14px' }}>
      <div className="row between wrap gap-3" style={{ alignItems: 'flex-start' }}>
        <div className="stack gap-1">
          <div className="row gap-2" style={{ alignItems: 'center' }}>
            <span style={{ fontWeight: 500 }}>{rule.name}</span>
            {!rule.enabled && <span className="cad-pill" data-tone="bad">off</span>}
          </div>
          <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
            When {TRIGGER_LABELS[rule.trigger].toLowerCase()},
            {rule.delaySeconds ? ` wait ${Math.round(rule.delaySeconds / 60)}m, then check` : ' check'}{' '}
            if {description.toLowerCase()}
          </span>
          <div className="row gap-1 wrap">
            {rule.actions.map((action, i) => (
              <span key={i} className="cad-pill">{ACTION_LABELS[action.type]}</span>
            ))}
          </div>
        </div>
        <div className="row gap-2 wrap">
          <button type="button" className="cad-btn" data-variant="text" onClick={() => setShowCanvas((v) => !v)}>
            {showCanvas ? 'View as list' : 'View as canvas'}
          </button>
          {canManage && (
            <>
              {/* Buttons rather than drag: order decides which rule wins when two would both
                  match, and a keyboard user should be able to change it too. */}
              <button
                type="button"
                className="cad-btn"
                data-variant="quiet"
                aria-label={`Move ${rule.name} earlier`}
                disabled={isFirst || reordering}
                onClick={onMoveUp}
              >
                ↑
              </button>
              <button
                type="button"
                className="cad-btn"
                data-variant="quiet"
                aria-label={`Move ${rule.name} later`}
                disabled={isLast || reordering}
                onClick={onMoveDown}
              >
                ↓
              </button>
              <button type="button" className="cad-btn" data-variant="text" onClick={() => setShowRuns((v) => !v)}>
                Activity
              </button>
              <button type="button" className="cad-btn" data-variant="text" onClick={onToggle}>
                {rule.enabled ? 'Turn off' : 'Turn on'}
              </button>
              <button type="button" className="cad-btn" data-variant="text" onClick={onEdit}>
                Edit
              </button>
              <button type="button" className="cad-btn" data-variant="text" onClick={onDelete}>
                Delete
              </button>
            </>
          )}
        </div>
      </div>

      {showCanvas && (
        <div style={{ marginTop: '10px' }}>
          <AutomationCanvas
            triggerLabel={TRIGGER_LABELS[rule.trigger]}
            chains={canvasChains}
            onNodeClick={canManage ? onEdit : undefined}
            height={estimateCanvasHeight(canvasChains)}
          />
        </div>
      )}

      {showRuns && (
        <div className="stack gap-1" style={{ marginTop: '10px' }}>
          {runs.isLoading ? (
            <p className="cad-muted">Loading…</p>
          ) : runs.isError ? (
            <p className="cad-muted">
              {runs.error instanceof ApiError ? runs.error.message : 'Could not load activity.'}
            </p>
          ) : (runs.data?.runs.length ?? 0) === 0 ? (
            <p className="cad-muted">Nothing has triggered this yet.</p>
          ) : (
            runs.data!.runs.map((run) => (
              <div key={run.id} className="row gap-2 wrap" style={{ fontSize: 'var(--cad-text-base)' }}>
                <span className="cad-muted">{new Date(run.createdAt).toLocaleString()}</span>
                <span className="cad-pill" data-tone={run.matched ? 'good' : undefined}>
                  {run.matched ? 'matched' : 'did not match'}
                </span>
                {run.actionsRun.map((a, i) => (
                  <span key={i} className="cad-pill" data-tone={a.ok ? undefined : 'bad'}>
                    {ACTION_LABELS[a.type as AutomationActionType] ?? a.type}: {a.ok ? (a.detail ?? 'ok') : a.detail}
                  </span>
                ))}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  )
}

/**
 * The starting-point picker shown before a blank rule form — a category, a handful of ready
 * rules within it, and a "start blank" escape hatch for anyone who already knows what they
 * want. Every card here is a real, valid `AutomationRuleDraft`; picking one only pre-fills the
 * editor, it does not save anything on its own.
 */
function TemplateGallery({
  onPick,
  onBlank,
  onCancel,
}: {
  onPick: (template: AutomationTemplate) => void
  onBlank: () => void
  onCancel: () => void
}) {
  const [category, setCategory] = useState<(typeof TEMPLATE_CATEGORIES)[number]>(TEMPLATE_CATEGORIES[0])
  const templates = AUTOMATION_TEMPLATES.filter((t) => t.category === category)

  return (
    <section className="cad-card">
      <div className="stack gap-3">
        <div className="row between wrap gap-2" style={{ alignItems: 'center' }}>
          <h2 className="cad-section-title">Start from a template</h2>
          <button type="button" className="cad-btn" data-variant="text" onClick={onCancel}>
            Cancel
          </button>
        </div>

        <div className="row gap-2 wrap">
          {TEMPLATE_CATEGORIES.map((c) => (
            <button
              key={c}
              type="button"
              className="cad-pill"
              data-tone={c === category ? 'accent' : undefined}
              onClick={() => setCategory(c)}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="stack gap-2">
          {templates.map((template) => (
            <div
              key={template.name}
              className="row between wrap gap-2"
              style={{ alignItems: 'center', border: '1px solid var(--cad-line)', borderRadius: '8px', padding: '10px' }}
            >
              <div className="stack gap-1">
                <span style={{ fontWeight: 500 }}>{template.name}</span>
                <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>{template.description}</span>
              </div>
              <button type="button" className="cad-btn" onClick={() => onPick(template)}>
                Use this
              </button>
            </div>
          ))}
        </div>

        <button type="button" className="cad-btn" data-variant="quiet" onClick={onBlank}>
          Start blank instead
        </button>
      </div>
    </section>
  )
}

const STEP_ICON_PATH: Record<'trigger' | 'criteria' | 'action', string> = {
  // A bolt — this step is what fires the rule.
  trigger: 'M9 1 3 9h4l-1 6 6-8H8l1-6Z',
  // A funnel — this step narrows the trigger down to a subset.
  criteria: 'M2 2h12l-5 6v5l-2 1V8L2 2Z',
  // A play arrow — this step is what runs once the rule matches.
  action: 'M4 2l9 6-9 6V2Z',
}

/** One labelled, icon-badged section of the rule editor — Trigger / Criteria / Actions — each
 *  visually its own step rather than three unlabelled blocks in one long scroll. Same shape this
 *  category's rule builders converge on (see the Periskope benchmark this was checked against). */
export function StepCard({
  icon,
  title,
  description,
  children,
}: {
  icon: 'trigger' | 'criteria' | 'action'
  title: string
  description: string
  children: ReactNode
}) {
  return (
    <div className="cad-step">
      <div className="cad-step-head">
        <span className="cad-step-icon" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 16 16">
            <path d={STEP_ICON_PATH[icon]} fill="currentColor" />
          </svg>
        </span>
        <div className="stack gap-1">
          <h3 className="cad-step-title">{title}</h3>
          <p className="cad-muted" style={{ margin: 0, fontSize: 'var(--cad-text-base)' }}>{description}</p>
        </div>
      </div>
      <div className="cad-step-body">{children}</div>
    </div>
  )
}

/**
 * The action-row list — type dropdown, context-sensitive value input, per-action cooldown —
 * shared between the single-rule `RuleEditor` below and the flow builder's step editor
 * (`FlowBuilder.tsx`), since a flow step's "what runs" half is exactly one rule's actions.
 */
export function ActionListEditor({
  actions,
  allowedActions,
  onChange,
  members,
}: {
  actions: AutomationAction[]
  allowedActions: readonly AutomationActionType[]
  onChange: (next: AutomationAction[]) => void
  /** Names for the "specific teammate" assignee picker. Without it (or while it's still
   *  loading) the picker falls back to a raw user-id field — worse, but never broken. */
  members?: { userId: string; name: string }[]
}) {
  const addAction = () => onChange([...actions, { type: allowedActions[0] }])
  const updateAction = (index: number, patch: Partial<AutomationAction>) =>
    onChange(actions.map((a, i) => (i === index ? { ...a, ...patch } : a)))
  const removeAction = (index: number) => onChange(actions.filter((_, i) => i !== index))

  return (
    <>
      {actions.map((action, index) => (
        <div key={index} className="row gap-2 wrap" style={{ alignItems: 'center' }}>
          <select
            className="cad-input"
            style={{ width: 'auto' }}
            value={action.type}
            onChange={(e) => updateAction(index, { type: e.target.value as AutomationActionType, value: undefined })}
          >
            {allowedActions.map((type) => (
              <option key={type} value={type}>{ACTION_LABELS[type]}</option>
            ))}
          </select>
          {action.type === 'assign_ticket' || action.type === 'assign_chat' ? (
            <>
              <select
                className="cad-input"
                style={{ width: 'auto' }}
                value={isAssignmentMode(action.value ?? '') ? action.value : action.value ? '__custom__' : ''}
                onChange={(e) =>
                  updateAction(index, { value: e.target.value === '__custom__' ? '' : e.target.value })
                }
              >
                <option value="">Choose how…</option>
                {ASSIGNMENT_MODES.map((mode) => (
                  <option key={mode} value={mode}>{ASSIGNMENT_MODE_LABEL[mode]}</option>
                ))}
                <option value="__custom__">Specific teammate (user id)</option>
              </select>
              {isAssignmentMode(action.value ?? '') && (
                <select
                  className="cad-input"
                  style={{ width: 'auto' }}
                  value={action.assignmentFilter ?? 'shift'}
                  onChange={(e) => updateAction(index, { assignmentFilter: e.target.value as (typeof ASSIGNMENT_FILTERS)[number] })}
                >
                  {ASSIGNMENT_FILTERS.map((filter) => (
                    <option key={filter} value={filter}>{ASSIGNMENT_FILTER_LABEL[filter]}</option>
                  ))}
                </select>
              )}
              {!isAssignmentMode(action.value ?? '') &&
                (members && members.length > 0 ? (
                  <select
                    className="cad-input"
                    style={{ width: 'auto' }}
                    value={action.value ?? ''}
                    onChange={(e) => updateAction(index, { value: e.target.value })}
                  >
                    <option value="">Choose a teammate…</option>
                    {members.map((m) => (
                      <option key={m.userId} value={m.userId}>{m.name}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    className="cad-input"
                    style={{ width: '220px' }}
                    placeholder="Assignee user id"
                    value={action.value ?? ''}
                    onChange={(e) => updateAction(index, { value: e.target.value })}
                  />
                ))}
            </>
          ) : (
            (NEEDS_VALUE.includes(action.type) || OPTIONAL_VALUE.includes(action.type)) && (
              <input
                className="cad-input"
                style={{ width: '220px' }}
                placeholder={VALUE_LABEL[action.type]}
                value={action.value ?? ''}
                onChange={(e) => updateAction(index, { value: e.target.value })}
              />
            )
          )}
          {action.type === 'create_ticket' && (
            <select
              className="cad-input"
              style={{ width: 'auto' }}
              value={action.priority ?? 'normal'}
              onChange={(e) => updateAction(index, { priority: e.target.value as AutomationAction['priority'] })}
            >
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          )}
          <label className="row gap-1" style={{ alignItems: 'center' }} title="Skip this action if it already ran for the same chat/ticket/message within this many minutes.">
            <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>Cooldown</span>
            <input
              className="cad-input"
              type="number"
              min={0}
              style={{ width: '70px' }}
              value={action.cooldownSeconds ? Math.round(action.cooldownSeconds / 60) : ''}
              placeholder="min"
              onChange={(e) => {
                const minutes = Math.max(0, Number(e.target.value) || 0)
                updateAction(index, { cooldownSeconds: minutes > 0 ? minutes * 60 : undefined })
              }}
            />
          </label>
          <button type="button" className="cad-btn" data-variant="text" onClick={() => removeAction(index)}>
            Remove
          </button>
        </div>
      ))}
      <button type="button" className="cad-btn" data-variant="quiet" onClick={addAction}>
        Add action
      </button>
      {actions.some((a) => a.type === 'send_email') && (
        <p className="cad-note">
          Sends via whichever email provider is configured for this deployment. If none is
          configured yet, this action is logged as failed instead of sending silently.
        </p>
      )}
    </>
  )
}

function RuleEditor({
  rule,
  template,
  onDone,
  onCancel,
}: {
  rule?: AutomationRuleView
  /** Seeds every field below the same way `rule` does when editing — a template is just a
   *  draft nobody has saved yet, fully editable from the moment it lands in the form. */
  template?: AutomationTemplate | null
  onDone: () => void
  onCancel: () => void
}) {
  const seed = rule ?? template?.draft
  const [name, setName] = useState(seed?.name ?? '')
  const [trigger, setTrigger] = useState<AutomationTrigger>(seed?.trigger ?? 'message.received')
  const [match, setMatch] = useState<'all' | 'any'>(seed?.match ?? 'all')
  const [conditions, setConditions] = useState<ConditionEntry[]>(seed?.conditions ?? [])
  const [actions, setActions] = useState<AutomationAction[]>(seed?.actions ?? [])
  const [enabled, setEnabled] = useState(rule?.enabled ?? true)
  const [delayMinutes, setDelayMinutes] = useState(
    Math.round((rule?.delaySeconds ?? template?.draft.delaySeconds ?? 0) / 60),
  )
  const team = useQuery({ queryKey: ['team'], queryFn: api.team })

  // Same fix as the assignee picker above: a "Labels" criteria condition typed as free text
  // silently never matches a real label it doesn't spell exactly right.
  const fields = fieldsForTrigger(trigger).map((f) =>
    f.key === 'labels' && team.data?.labels.length ? { ...f, options: team.data.labels.map((l) => l.name) } : f,
  )
  const allowedActions = ACTIONS_FOR_ENTITY[TRIGGER_ENTITY[trigger]]
  const draft = {
    name,
    trigger,
    match,
    conditions,
    actions,
    enabled,
    delaySeconds: delayMinutes > 0 ? delayMinutes * 60 : null,
  }
  const problems = validateAutomationRule(draft)

  const save = useMutation({
    mutationFn: () => (rule ? api.updateAutomationRule(rule.id, draft) : api.createAutomationRule(draft)),
    onSuccess: onDone,
  })

  const error = save.error
  const errorMessage = error instanceof ApiError || error instanceof Error ? error.message : undefined

  return (
    <section className="cad-card">
      <div className="stack gap-4">
        <h2 className="cad-section-title">{rule ? 'Edit rule' : 'New rule'}</h2>

        <StepCard icon="trigger" title="Trigger" description="What starts this rule">
          <div className="field-grid">
            <div className="cad-field">
              <label htmlFor="aru-name">Name</label>
              <input id="aru-name" className="cad-input" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="cad-field">
              <label htmlFor="aru-trigger">When</label>
              <select
                id="aru-trigger"
                className="cad-input"
                value={trigger}
                onChange={(e) => {
                  const next = e.target.value as AutomationTrigger
                  setTrigger(next)
                  setConditions([])
                  setActions([])
                }}
              >
                {(Object.keys(TRIGGER_LABELS) as AutomationTrigger[]).map((t) => (
                  <option key={t} value={t}>{TRIGGER_LABELS[t]}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="cad-field" style={{ maxWidth: '260px' }}>
            <label htmlFor="aru-delay">Check conditions after (minutes)</label>
            <input
              id="aru-delay"
              className="cad-input"
              type="number"
              min={0}
              value={delayMinutes}
              onChange={(e) => setDelayMinutes(Math.max(0, Number(e.target.value) || 0))}
            />
            <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
              0 checks immediately, same as before. A wait re-reads the ticket's live status
              instead of the moment it triggered — useful for "still open after 15 minutes".
            </span>
          </div>
        </StepCard>

        <StepCard icon="criteria" title="Criteria" description="Narrow the trigger down to a subset — at least one condition is required, or the rule matches nothing">
          {conditions.length === 0 ? (
            <button
              type="button"
              className="cad-btn"
              data-variant="quiet"
              onClick={() => setConditions([{ field: fields[0].key, operator: 'is', value: '' }])}
            >
              Add condition
            </button>
          ) : (
            <SegmentBuilder
              fields={fields}
              match={match}
              conditions={conditions}
              onChange={(next) => {
                setMatch(next.match)
                setConditions(next.conditions)
              }}
            />
          )}
        </StepCard>

        <StepCard icon="action" title="Actions" description="What runs once the trigger fires and the criteria match">
          <ActionListEditor
            actions={actions}
            allowedActions={allowedActions}
            onChange={setActions}
            members={team.data?.members}
          />
        </StepCard>

        <label className="row gap-2" style={{ alignItems: 'center', cursor: 'pointer' }}>
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          <span className="cad-muted">Enabled</span>
        </label>

        {errorMessage && <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>{errorMessage}</p>}

        {/* These were computed and used to disable Save, but never rendered — so a rule missing
            a condition or an action value left the button dead with nothing on screen saying
            which field was at fault. `validateAutomationRule` has always returned sentences
            written for exactly this; Workflows.tsx renders its equivalent the same way. */}
        {problems.map((problem) => (
          <p key={problem} className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
            {problem}
          </p>
        ))}

        <div className="row gap-2 wrap">
          <button
            type="button"
            className="cad-btn"
            disabled={problems.length > 0 || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : 'Save rule'}
          </button>
          <button type="button" className="cad-btn" data-variant="quiet" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </section>
  )
}
